import { ArrowLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { AddressSection } from '@/features/billboards/components/detail/AddressSection';
import { DetailHero } from '@/features/billboards/components/detail/DetailHero';
import {
  DetailLoadingState,
  DetailNotFoundState,
} from '@/features/billboards/components/detail/DetailPageStates';
import { GuidelinesSection } from '@/features/billboards/components/detail/GuidelinesSection';
import { MobileCampaignBar } from '@/features/billboards/components/detail/MobileCampaignBar';
import { ScreenInfoSection } from '@/features/billboards/components/detail/ScreenInfoSection';
import { VenueSection } from '@/features/billboards/components/detail/VenueSection';
import { NearestScreenLocations } from '@/features/billboards/components/NearestScreenLocations';
import { useBillboardDetailPage } from '@/features/billboards/hooks/useBillboardDetailPage';
import { Navbar } from '@/features/landing/components/Navbar';
import { routes } from '@/shared/config/routes';

type BillboardDetailPageProps = {
  /** When true, render inside AppLayout (no marketing Navbar). */
  embedded?: boolean;
};

type DetailLocationState = {
  from?: string;
};

function resolveEmbeddedBackTarget(from?: string) {
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('/screens/')) {
    return from;
  }
  return routes.screens;
}

export function BillboardDetailPage({
  embedded = false,
}: BillboardDetailPageProps) {
  const location = useLocation();
  const {
    navigate,
    billboard,
    isPending,
    isError,
    hasToken,
    startDate,
    setStartDate,
    minStartDate,
    isScreenOffline,
    selectedImage,
    setSelectedImage,
    media,
    currency,
    coordinates,
    fullAddress,
    canCreate,
    handleCreateCampaign,
    isClient,
  } = useBillboardDetailPage();

  const fromPath = (location.state as DetailLocationState | null)?.from;
  const detailBasePath = embedded ? '/screens' : '/billboard';
  const backTarget = embedded ? resolveEmbeddedBackTarget(fromPath) : -1;

  if (isPending) return <DetailLoadingState embedded={embedded} />;
  if (isError || !billboard) {
    return (
      <DetailNotFoundState
        embedded={embedded}
        onBackHome={() =>
          navigate(embedded ? resolveEmbeddedBackTarget(fromPath) : '/')
        }
      />
    );
  }


  return (
    <div
      className={
        embedded
          ? 'w-full pb-28 text-[#3A3660]'
          : 'min-h-screen bg-[#F3F2F9] pb-28 text-[#3A3660]'
      }
    >
      {!embedded ? (
        <Navbar homeHref="/" city={billboard.address?.city} authMode="signin" />
      ) : null}

      <main
        className={
          embedded
            ? 'mx-auto w-full max-w-[1120px] px-1 pt-1 sm:px-2 sm:pt-2'
            : 'mx-auto max-w-[1120px] px-4 pt-4 sm:px-6 sm:pt-6'
        }
      >
        
        <button
          type="button"
          onClick={() => {
            if (typeof backTarget === 'number') navigate(backTarget);
            else navigate(backTarget);
          }}
          className="mb-4 inline-flex cursor-pointer items-center gap-1.5 rounded-full border-none bg-transparent px-0 py-1 text-sm font-semibold text-[#6A6688] transition hover:text-[#3A3660]"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <DetailHero
          billboard={billboard}
          media={media}
          selectedImage={selectedImage}
          onSelectImage={setSelectedImage}
          currency={currency}
          startDate={startDate}
          minStartDate={minStartDate}
          isScreenOffline={isScreenOffline}
          hasToken={hasToken}
          canCreate={canCreate}
          isClient={isClient}
          onStartDateChange={setStartDate}
          onCreate={handleCreateCampaign}
        />

        <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-5 lg:grid-cols-2">
          <ScreenInfoSection billboard={billboard} />
          <VenueSection billboard={billboard} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-5 lg:grid-cols-2">
          <GuidelinesSection
            title="Screen guidelines"
            subtitle="Rules specific to this display"
            items={billboard.screenGuideline}
            variant="screen"
          />
          <GuidelinesSection
            title="Government guidelines"
            subtitle="Legal requirements for this location"
            items={billboard.governmentGuidelines}
            variant="government"
          />
        </div>

        <AddressSection
          billboard={billboard}
          fullAddress={fullAddress}
          lat={coordinates?.lat}
          lng={coordinates?.lng}
        />

        <NearestScreenLocations
          locations={billboard.nearestBillboards || []}
          detailBasePath={detailBasePath}
          backTo={typeof fromPath === 'string' ? fromPath : undefined}
        />
      </main>

      <MobileCampaignBar
        basePrice={billboard.basePrice}
        currency={currency}
        hasToken={hasToken}
        canCreate={canCreate}
        isClient={isClient}
        onCreate={handleCreateCampaign}
      />
    </div>
  );
}
